import { useEffect, useId, useRef, useState } from 'react';
import type { ClientRef } from '../types';
import { matchClients, readSort, SORT_COLUMNS, SORT_LABELS, withParams, type SortColumn } from '../lib/listSort';
import { useAuth } from '../context/AuthContext';
import { isDtStaff } from '../lib/staff';

/**
 * Filtres ajoutés à la vue courante (client, texte libre) et menu « Trier
 * par » des écrans étroits. Tout est dans l'URL : `id_client`, `q`, `sort`,
 * `dir`, à côté de `preset` (la vue), que l'on ne touche pas.
 */
export function TicketListControls({
  params,
  setParams,
  clients,
}: {
  params: URLSearchParams;
  setParams: (next: URLSearchParams) => void;
  clients: readonly ClientRef[];
}) {
  const idClient = Number(params.get('id_client')) || null;
  const q = params.get('q') ?? '';
  // Points hors TMA (retour 6) : filtre réservé au staff, comme le badge.
  const { user } = useAuth();
  const staff = isDtStaff(user);
  const horsTma = params.get('hors_tma') ?? '';
  const hasFilters = idClient !== null || q !== '' || horsTma !== '';

  // Texte libre : appliqué après une courte pause de frappe (ou Entrée).
  const [text, setText] = useState(q);
  useEffect(() => setText(q), [q]);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  useEffect(() => {
    if (text.trim() === q) return;
    const t = setTimeout(() => setParams(withParams(paramsRef.current, { q: text.trim() || null })), 400);
    return () => clearTimeout(t);
  }, [text, q, setParams]);

  const { sort, dir } = readSort(params);

  return (
    <div className="tlist-controls">
      <form
        className="tlist-filters"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setParams(withParams(params, { q: text.trim() || null }));
        }}
      >
        {/* Le choix du client n'a de sens qu'avec plusieurs clients visibles. */}
        {clients.length > 1 && (
          <ClientCombobox
            clients={clients}
            selectedId={idClient}
            onSelect={(id) => setParams(withParams(params, { id_client: id === null ? null : String(id) }))}
          />
        )}
        <input
          type="search"
          className="tlist-input"
          aria-label="Rechercher dans le titre ou le n° de PS"
          placeholder="Titre ou n° de PS"
          value={text}
          maxLength={200}
          onChange={(e) => setText(e.target.value)}
        />
        {staff && (
          <select
            className="tlist-input tlist-select"
            aria-label="Points TMA ou hors TMA"
            value={horsTma}
            onChange={(e) => setParams(withParams(params, { hors_tma: e.target.value || null }))}
          >
            <option value="">TMA et hors TMA</option>
            <option value="1">Hors TMA</option>
            <option value="0">TMA seulement</option>
          </select>
        )}
        {hasFilters && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setText('');
              setParams(withParams(params, { id_client: null, q: null, hors_tma: null }));
            }}
          >
            Effacer les filtres
          </button>
        )}
      </form>

      {/* Écran étroit : les en-têtes de colonne sont masqués, ce menu les remplace. */}
      <div className="trow-c">
        <div className="tlist-sort-compact">
          <label htmlFor="tri-compact">Trier par</label>
          <select
            id="tri-compact"
            value={sort ? `${sort}:${dir}` : ''}
            onChange={(e) => {
              const [s, d] = e.target.value.split(':');
              setParams(withParams(params, { sort: s || null, dir: s ? d ?? 'asc' : null }));
            }}
          >
            <option value="">Ordre par défaut</option>
            {SORT_COLUMNS.flatMap((c: SortColumn) => [
              <option key={`${c}:asc`} value={`${c}:asc`}>
                {SORT_LABELS[c]} ▲
              </option>,
              <option key={`${c}:desc`} value={`${c}:desc`}>
                {SORT_LABELS[c]} ▼
              </option>,
            ])}
          </select>
        </div>
      </div>
    </div>
  );
}

/** Champ client à saisie assistée : « alg » propose Algoé. */
function ClientCombobox({
  clients,
  selectedId,
  onSelect,
}: {
  clients: readonly ClientRef[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) {
  const selected = clients.find((c) => c.id_client === selectedId) ?? null;
  const [text, setText] = useState(selected?.nom_client ?? '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  useEffect(() => setText(selected?.nom_client ?? ''), [selected?.nom_client]);

  const matches = matchClients(clients, text === selected?.nom_client ? '' : text);

  const choose = (c: ClientRef | null) => {
    setOpen(false);
    setText(c?.nom_client ?? '');
    onSelect(c ? c.id_client : null);
  };

  return (
    <div className="tlist-combo">
      <input
        type="text"
        className="tlist-input"
        role="combobox"
        aria-label="Client"
        aria-autocomplete="list"
        aria-expanded={open && matches.length > 0}
        aria-controls={listId}
        aria-activedescendant={open && matches[active] ? `${listId}-${matches[active]!.id_client}` : undefined}
        placeholder="Client"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setActive(0);
          setOpen(true);
          if (e.target.value === '' && selectedId !== null) onSelect(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter' && open && matches[active]) {
            e.preventDefault();
            choose(matches[active]!);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && matches.length > 0 && (
        <ul id={listId} role="listbox" className="tlist-combo-list">
          {matches.map((c, i) => (
            <li
              key={c.id_client}
              id={`${listId}-${c.id_client}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'is-active' : undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(c);
              }}
            >
              {c.nom_client}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
